/**
 * 시간 축 — 편성 해석의 기준.
 *
 * 설계 원칙: 이 모듈의 어떤 함수도 `Date.now()` 를 부르지 않는다. 시각은 항상 인자로
 * 주입된다. 해석이 암묵적 "지금"에 의존하면 재현도 테스트도 불가능해지기 때문이다.
 *
 * 여기서 TalkOne 구현의 결함 두 가지를 해소한다:
 *   R1 — 심야 데이파팅(22:00~02:00)이 영원히 매칭되지 않던 문제.
 *   R8 — 잘못된 "HH:MM" 입력이 조용히 자정(0)으로 해석되던 문제.
 */
/** 하루 전체(분). `end: '24:00'` 이 정규화되는 값. */
export const MINUTES_PER_DAY = 1440;
const WEEKDAY_INDEX = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};
/**
 * 타임존별 Intl 포매터 캐시.
 * 편성 해석은 디스플레이 폴링마다 호출되는 뜨거운 경로다(5,000대 × 30초 ≈ 167 rps).
 * `Intl.DateTimeFormat` 생성은 비싸므로 타임존당 1회만 만든다.
 */
const formatterCache = new Map();
function formatterFor(timeZone) {
    const cached = formatterCache.get(timeZone);
    if (cached)
        return cached;
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    formatterCache.set(timeZone, fmt);
    return fmt;
}
/**
 * 유효하다고 확인한 타임존 — `isValidTimeZone` 이 같은 이름을 두 번 확인하지 않게.
 *
 * **해석의 시각 변환마다 불린다**(`zonedFieldsAt` 등). 확인하는 길이 `Intl.DateTimeFormat` 을 새로 만드는 것뿐이라,
 * 위의 포매터 캐시를 두고도 변환마다 포매터를 하나씩 더 만들고 있었다 — 편성 저장 직후 화면 500대가 다시 물을
 * 때 서버 CPU 의 4분의 1이 여기였다(2026-10-12 측정). 유효한 이름만 적는다: 잘못된 이름은 저장 때 걸러져
 * 드물고, 적어 두면 아무 문자열이나 쌓일 수 있다. 같은 까닭으로 유효한 이름도 상한까지만 적는다(대소문자만
 * 다른 이름도 유효하다).
 */
const validTimeZones = new Set();
const VALID_TIME_ZONES_CAP = 1024;
/** IANA 타임존 식별자로 유효한가. 스키마 검증과 해석의 시각 변환에서 사용. */
export function isValidTimeZone(tz) {
    if (!tz)
        return false;
    if (validTimeZones.has(tz))
        return true;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
    }
    catch {
        return false;
    }
    if (validTimeZones.size < VALID_TIME_ZONES_CAP)
        validTimeZones.add(tz);
    return true;
}
/**
 * 주어진 시각을 특정 타임존의 벽시계 필드로 변환.
 *
 * 유효하지 않은 타임존은 **UTC 로 폴백**한다(예외를 던지지 않음). 해석 엔진은 어떤
 * 입력에도 결과를 내야 하기 때문이다. 잘못된 타임존은 저장 시점 스키마 검증에서
 * 걸러지므로, 이 폴백이 실제로 쓰이는 경로는 데이터 손상뿐이다.
 */
export function zonedFieldsAt(at, timeZone) {
    const tz = isValidTimeZone(timeZone) ? timeZone : 'UTC';
    const parts = formatterFor(tz).formatToParts(at);
    let day = 0;
    let hour = 0;
    let minute = 0;
    for (const p of parts) {
        if (p.type === 'weekday')
            day = WEEKDAY_INDEX[p.value] ?? 0;
        else if (p.type === 'hour')
            hour = Number.parseInt(p.value, 10) % 24;
        else if (p.type === 'minute')
            minute = Number.parseInt(p.value, 10);
    }
    return { day, minutes: hour * 60 + minute };
}
/**
 * 달력 필드까지 뽑는 포매터. `formatterFor` 와 나누어 두는 이유는 뜨거운 경로 때문이다 —
 * 편성 해석은 요일과 분만 필요하고(폴링마다 호출된다), 달력 날짜는 미리보기처럼
 * 하루를 통째로 다루는 드문 경로에서만 쓴다.
 */
const calendarCache = new Map();
function calendarFormatterFor(timeZone) {
    const cached = calendarCache.get(timeZone);
    if (cached)
        return cached;
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    calendarCache.set(timeZone, fmt);
    return fmt;
}
/** 이 시각에 해당 타임존이 UTC 보다 몇 분 앞서 있는가. 서머타임이 반영된 값. */
function offsetMinutesAt(at, timeZone) {
    const parts = calendarFormatterFor(timeZone).formatToParts(at);
    const field = {};
    for (const p of parts) {
        if (p.type !== 'literal')
            field[p.type] = Number.parseInt(p.value, 10);
    }
    const asIfUtc = Date.UTC(field.year ?? 1970, (field.month ?? 1) - 1, field.day ?? 1, (field.hour ?? 0) % 24, field.minute ?? 0, field.second ?? 0);
    return (asIfUtc - at.getTime()) / 60_000;
}
/** `YYYY-MM-DD` 형식. */
export const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * 그 타임존에서 본 달력 날짜(`YYYY-MM-DD`).
 *
 * UTC 날짜와 다를 수 있다 — 서울의 6월 1일 새벽 3시는 UTC 로는 아직 5월 31일이다.
 * "오늘 편성" 을 묻는 곳은 반드시 이쪽을 써야 한다.
 */
export function zonedYmd(at, timeZone) {
    const tz = isValidTimeZone(timeZone) ? timeZone : 'UTC';
    const parts = calendarFormatterFor(tz).formatToParts(at);
    const field = {};
    for (const p of parts) {
        if (p.type !== 'literal')
            field[p.type] = p.value;
    }
    return `${field.year ?? '1970'}-${field.month ?? '01'}-${field.day ?? '01'}`;
}
/**
 * 그 타임존의 그 날 **자정**에 해당하는 실제 시각.
 *
 * 하루를 훑는 쪽(미리보기)이 UTC 자정에서 시작하면 보여주는 하루가 오프셋만큼 밀린다.
 * 서울이면 아홉 시간 — "월요일" 을 요청했는데 월 09:00 ~ 화 09:00 을 훑게 되고,
 * **월요일 새벽 규칙이 창 밖으로 빠진다.** 심야 편성을 눈으로 검증하라고 만든 기능이
 * 정작 심야에서 틀리므로, 기준 자정은 반드시 그 타임존에서 잡는다.
 *
 * 오프셋은 시각에 따라 달라지므로(서머타임) 두 번 접근한다. 자정이 서머타임 전환으로
 * 존재하지 않는 지역에서는 전환 직후의 시각으로 수렴한다 — 그 날의 실제 시작이다.
 *
 * 형식이 어긋나면 **null**. 조용히 오늘로 떨어지지 않는다.
 */
export function zonedDayStart(ymd, timeZone) {
    const m = YMD_PATTERN.exec(ymd);
    if (!m)
        return null;
    const tz = isValidTimeZone(timeZone) ? timeZone : 'UTC';
    const wall = Date.UTC(Number.parseInt(m[1], 10), Number.parseInt(m[2], 10) - 1, Number.parseInt(m[3], 10));
    if (Number.isNaN(wall))
        return null;
    let instant = wall - offsetMinutesAt(new Date(wall), tz) * 60_000;
    instant = wall - offsetMinutesAt(new Date(instant), tz) * 60_000;
    return new Date(instant);
}
/**
 * 그 타임존에서 `ymd` 다음 날 자정. 하루의 끝이다.
 *
 * 24시간을 더하지 않는 이유: 서머타임이 있는 지역의 하루는 23시간이거나 25시간이다.
 * 고정 24시간으로 훑으면 그 날 하루가 한 시간 잘리거나 다음 날 한 시간이 섞여 들어온다.
 */
export function zonedNextDayStart(ymd, timeZone) {
    const start = zonedDayStart(ymd, timeZone);
    if (!start)
        return null;
    // 정오에서 하루를 더한다 — 자정 근처에서 더하면 서머타임 전환에 걸려 날짜가 어긋난다.
    const noonNextDay = new Date(start.getTime() + 36 * 3600_000);
    return zonedDayStart(zonedYmd(noonNextDay, timeZone), timeZone);
}
/** `HH:MM` 형식. 00:00 ~ 24:00 (24:00 은 하루의 끝을 뜻하는 종료 시각 전용 값). */
export const HHMM_PATTERN = /^([01]\d|2[0-4]):([0-5]\d)$/;
/**
 * `HH:MM` → 자정 기준 분. 형식이 어긋나면 **null**.
 *
 * TalkOne 구현은 파싱 실패를 0(자정)으로 처리해 `"9:5"`·`"25:00"` 같은 오타가 조용히
 * 자정으로 해석됐다(R8). 여기서는 실패를 명시적으로 드러내고, 스키마가 저장 시점에 막는다.
 */
export function parseHhmm(value) {
    const m = HHMM_PATTERN.exec(value);
    if (!m)
        return null;
    const hours = Number.parseInt(m[1], 10);
    const minutes = Number.parseInt(m[2], 10);
    // 24:MM 은 24:00 만 허용 — 24:30 같은 값은 의미가 없다.
    if (hours === 24 && minutes !== 0)
        return null;
    return hours * 60 + minutes;
}
/**
 * 구간이 주어진 벽시계 시각을 포함하는가.
 *
 * **심야 구간(R1).** `end <= start` 이면 자정을 넘는 것으로 보고 두 구간으로 분해한다:
 *   - `days` 에 속한 날의 `[start, 24:00)`
 *   - `days` 에 속한 날의 **다음 날** `[00:00, end)`
 *
 * 즉 `days:[1](월) 22:00~02:00` 은 "월 22시부터 화 2시까지"를 뜻한다. TalkOne 구현은
 * `minutes < start || minutes >= end` 단일 비교라 이 구간이 **영원히 매칭되지 않았다.**
 *
 * `start === end` 는 스키마가 거부하지만, 손상된 데이터가 들어와도 조용히 사라지지
 * 않도록 여기서는 **해당 요일 24시간 전체**로 해석한다.
 */
export function matchesDaypart(daypart, fields) {
    const start = parseHhmm(daypart.start);
    const end = parseHhmm(daypart.end);
    // 파싱 불가는 매칭 없음 — 조용히 자정으로 취급하지 않는다(R8).
    if (start === null || end === null)
        return false;
    const days = daypart.days;
    if (days.length === 0)
        return false;
    const onDay = days.includes(fields.day);
    if (start === end)
        return onDay; // 손상 데이터 방어 — 종일로 해석
    if (end > start)
        return onDay && fields.minutes >= start && fields.minutes < end;
    // 자정을 넘는 구간.
    if (onDay && fields.minutes >= start)
        return true;
    const previousDay = (fields.day + 6) % 7;
    return days.includes(previousDay) && fields.minutes < end;
}
/** 구간의 하루 내 길이(분). 심야 구간도 올바른 길이를 낸다. 진단·검증용. */
export function daypartDurationMinutes(daypart) {
    const start = parseHhmm(daypart.start);
    const end = parseHhmm(daypart.end);
    if (start === null || end === null)
        return 0;
    if (start === end)
        return MINUTES_PER_DAY;
    return end > start ? end - start : MINUTES_PER_DAY - start + end;
}
/**
 * 운영 시간 — 구간 중 하나라도 이 시각을 품는가.
 *
 * 스케줄 규칙·캠페인 데이파팅과 **같은 매칭 함수**를 쓴다(자정을 넘는 구간 포함). 운영 시간만
 * 다른 규칙으로 읽으면 "스케줄은 22:00~02:00 에 트는데 운영 시간은 자정에 끝난다" 같은 어긋남이
 * 화면에서야 드러난다.
 */
export function withinDayparts(dayparts, at, timeZone) {
    const fields = zonedFieldsAt(at, timeZone);
    return dayparts.some((daypart) => matchesDaypart(daypart, fields));
}
/**
 * 운영 시간이 다음에 열리는 시각 — `after` 뒤로 `maxDays` 일 안에서. 이미 열려 있으면 `after`, 못 찾으면 null.
 *
 * **왜 필요한가.** 화면은 서버가 미리 풀어 준 시간표(약 2시간)로 경계를 넘는다. 밤에 문을 닫은 채
 * 망이 끊기면 시간표가 "쉼" 으로 끝나 있어, 아침이 와도 다시 열 근거가 없다 — 망이 돌아올 때까지
 * 로비가 검은 채로 남는다. 서버가 다음 여는 시각의 편성을 시간표 끝에 붙여 주려면 그 시각을 알아야 한다.
 *
 * 규칙의 시작 시각만 후보로 본다 — 닫혀 있다가 열리는 순간은 언제나 어떤 규칙의 시작이다.
 * 서머타임 전환일에는 한 시간 어긋날 수 있다(그 시각은 다음 폴링이 바로잡는다).
 */
export function nextOpening(dayparts, after, timeZone, maxDays = 8) {
    if (withinDayparts(dayparts, after, timeZone))
        return after;
    let ymd = zonedYmd(after, timeZone);
    for (let i = 0; i <= maxDays; i += 1) {
        const dayStart = zonedDayStart(ymd, timeZone);
        if (!dayStart)
            return null;
        // 정오의 요일 — 자정 근처는 서머타임 전환에 걸릴 수 있다.
        const weekday = zonedFieldsAt(new Date(dayStart.getTime() + 12 * 3600_000), timeZone).day;
        let best = null;
        for (const daypart of dayparts) {
            if (!daypart.days.includes(weekday))
                continue;
            const start = parseHhmm(daypart.start);
            if (start === null)
                continue;
            const candidate = dayStart.getTime() + start * 60_000;
            if (candidate > after.getTime() && (best === null || candidate < best))
                best = candidate;
        }
        if (best !== null)
            return new Date(best);
        const next = zonedNextDayStart(ymd, timeZone);
        if (!next)
            return null;
        ymd = zonedYmd(next, timeZone);
    }
    return null;
}
//# sourceMappingURL=time.js.map